import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * AnalyticsContext — persists Analytics page state across navigation AND F5 refresh.
 * Lives above the router so it never unmounts when switching pages.
 * State is backed up to sessionStorage so it survives tab refresh.
 *
 * Mirrors LiveTrackingContext/GeofencingContext: client-only, zero server load.
 * Only persists UI state (filters, viewport, toggles, selection IDs) — never raw data arrays.
 *
 * Date handling: Dates are stored as ISO strings (`*Iso`) and rehydrated to Date objects
 * by the Analytics page on initial mount. Setter helpers accept Date|null and persist ISO.
 */
const AnalyticsContext = createContext(null);

export const useAnalytics = () => {
  const ctx = useContext(AnalyticsContext);
  if (!ctx) throw new Error('useAnalytics must be used within AnalyticsProvider');
  return ctx;
};

const STORAGE_KEY = 'an_state';
const SYDNEY = { lat: -33.8688, lng: 151.2093 };

const loadPersistedState = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return null;
};

// Helper: serialize a {start, end} Date range to ISO strings (null-safe)
export const serializeDateRange = (range) => {
  if (!range) return { start: null, end: null };
  const toIso = (d) => {
    if (!d) return null;
    if (d instanceof Date) return d.toISOString();
    if (typeof d === 'string') return d;
    return null;
  };
  return { start: toIso(range.start), end: toIso(range.end) };
};

// Helper: hydrate ISO strings back to Date objects (null-safe, falls back to `fallback` range)
export const hydrateDateRange = (iso, fallback) => {
  if (!iso || (!iso.start && !iso.end)) return fallback;
  const fromIso = (s) => {
    if (!s) return null;
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  };
  const start = fromIso(iso.start);
  const end = fromIso(iso.end);
  if (!start && !end) return fallback;
  return { start, end };
};

export const AnalyticsProvider = ({ children }) => {
  const saved = loadPersistedState();

  // ── Tab state ──
  const [persistedActiveTab, setPersistedActiveTab] = useState(saved?.activeTab || null);

  // ── Mobile toggles ──
  const [mobileHistoryView, setMobileHistoryView] = useState(saved?.mobileHistoryView || 'list');
  const [mobileInvestigationView, setMobileInvestigationView] = useState(saved?.mobileInvestigationView || 'search');

  // ── Events tab filters ──
  const [eventFilter, setEventFilter] = useState(saved?.eventFilter || 'all');
  const [eventStatusFilter, setEventStatusFilter] = useState(saved?.eventStatusFilter || 'all');
  const [eventSeverityFilter, setEventSeverityFilter] = useState(saved?.eventSeverityFilter || 'all');
  const [eventSearch, setEventSearch] = useState(saved?.eventSearch || '');
  const [eventDatePreset, setEventDatePreset] = useState(saved?.eventDatePreset || 'all');
  const [eventDateRangeIso, setEventDateRangeIso] = useState(saved?.eventDateRangeIso || { start: null, end: null });
  const [eventsView, setEventsView] = useState(saved?.eventsView || 'occurrences');
  const [eventsPage, setEventsPage] = useState(saved?.eventsPage || 0);

  // ── Alerts tab filters ──
  const [alertFilter, setAlertFilter] = useState(saved?.alertFilter || 'all');
  const [alertSeverityFilter, setAlertSeverityFilter] = useState(saved?.alertSeverityFilter || 'all');
  const [alertTypeFilter, setAlertTypeFilter] = useState(saved?.alertTypeFilter || 'all');
  const [alertSearch, setAlertSearch] = useState(saved?.alertSearch || '');
  const [alertDatePreset, setAlertDatePreset] = useState(saved?.alertDatePreset || 'all');
  const [alertDateRangeIso, setAlertDateRangeIso] = useState(saved?.alertDateRangeIso || { start: null, end: null });

  // ── Event Rules sub-view (used by EventRulesManager child component) ──
  const [eventRulesSearchTerm, setEventRulesSearchTerm] = useState(saved?.eventRulesSearchTerm || '');
  const [eventRulesSelectedIds, setEventRulesSelectedIds] = useState(saved?.eventRulesSelectedIds || []);

  // ── History tab filters ──
  const [historyDevice, setHistoryDevice] = useState(saved?.historyDevice || '');
  const [historyTimezoneMode, setHistoryTimezoneMode] = useState(saved?.historyTimezoneMode || 'device');
  const [historyDatePreset, setHistoryDatePreset] = useState(saved?.historyDatePreset || 'today');
  const [historyDateRangeIso, setHistoryDateRangeIso] = useState(saved?.historyDateRangeIso || { start: null, end: null });
  const [splitMode, setSplitMode] = useState(saved?.splitMode || 'none');
  const [stopThresholdSecs, setStopThresholdSecs] = useState(saved?.stopThresholdSecs || 120);
  const [idleThresholdSecs, setIdleThresholdSecs] = useState(saved?.idleThresholdSecs || 120);

  // ── History map viewport ──
  const [historyMapCenter, setHistoryMapCenter] = useState(saved?.historyMapCenter || SYDNEY);
  const [historyMapZoom, setHistoryMapZoom] = useState(saved?.historyMapZoom || 12);
  const [historyShowPOI, setHistoryShowPOI] = useState(saved?.historyShowPOI || false);
  const [historyDarkMode, setHistoryDarkMode] = useState(saved?.historyDarkMode || false);

  // ── History overlay toggles ──
  const [showRouteOverlay, setShowRouteOverlay] = useState(saved?.showRouteOverlay !== undefined ? saved.showRouteOverlay : true);
  const [showDataPoints, setShowDataPoints] = useState(saved?.showDataPoints || false);
  const [showIdleIcons, setShowIdleIcons] = useState(saved?.showIdleIcons !== undefined ? saved.showIdleIcons : true);
  const [showStoppedIcons, setShowStoppedIcons] = useState(saved?.showStoppedIcons !== undefined ? saved.showStoppedIcons : true);
  const [showEventIcons, setShowEventIcons] = useState(saved?.showEventIcons !== undefined ? saved.showEventIcons : true);
  const [snapToRoadEnabled, setSnapToRoadEnabled] = useState(saved?.snapToRoadEnabled || false);

  // ── Forensic panel preferences ──
  const [forensicActiveTab, setForensicActiveTab] = useState(saved?.forensicActiveTab || 'telemetry');
  const [messageViewMode, setMessageViewMode] = useState(saved?.messageViewMode || 'mapped');
  const [graphEnabledFields, setGraphEnabledFields] = useState(saved?.graphEnabledFields || ['speed', 'ignition']);
  const [graphCustomColors, setGraphCustomColors] = useState(saved?.graphCustomColors || {});

  // ── Investigation filters ──
  const [invAddress, setInvAddress] = useState(saved?.invAddress || '');
  const [invLat, setInvLat] = useState(saved?.invLat ?? null);
  const [invLng, setInvLng] = useState(saved?.invLng ?? null);
  const [invDatePreset, setInvDatePreset] = useState(saved?.invDatePreset || 'today');
  const [invDateRangeIso, setInvDateRangeIso] = useState(saved?.invDateRangeIso || { start: null, end: null });
  const [invRadius, setInvRadius] = useState(saved?.invRadius || 500);

  // ── Reports preferences ──
  const [selectedReportType, setSelectedReportType] = useState(saved?.selectedReportType || 'driving_summary');
  const [reportFormat, setReportFormat] = useState(saved?.reportFormat || 'pdf');
  const [reportDateRangeIso, setReportDateRangeIso] = useState(saved?.reportDateRangeIso || { start: null, end: null });

  // ── Persist to sessionStorage on changes ──
  useEffect(() => {
    const state = {
      activeTab: persistedActiveTab,
      mobileHistoryView, mobileInvestigationView,
      eventFilter, eventStatusFilter, eventSeverityFilter, eventSearch, eventDatePreset, eventDateRangeIso, eventsView, eventsPage,
      alertFilter, alertSeverityFilter, alertTypeFilter, alertSearch, alertDatePreset, alertDateRangeIso,
      eventRulesSearchTerm, eventRulesSelectedIds,
      historyDevice, historyTimezoneMode, historyDatePreset, historyDateRangeIso,
      splitMode, stopThresholdSecs, idleThresholdSecs,
      historyMapCenter, historyMapZoom, historyShowPOI, historyDarkMode,
      showRouteOverlay, showDataPoints, showIdleIcons, showStoppedIcons, showEventIcons, snapToRoadEnabled,
      forensicActiveTab, messageViewMode, graphEnabledFields, graphCustomColors,
      invAddress, invLat, invLng, invDatePreset, invDateRangeIso, invRadius,
      selectedReportType, reportFormat, reportDateRangeIso,
    };
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* ignore */ }
  }, [
    persistedActiveTab,
    mobileHistoryView, mobileInvestigationView,
    eventFilter, eventStatusFilter, eventSeverityFilter, eventSearch, eventDatePreset, eventDateRangeIso, eventsView, eventsPage,
    alertFilter, alertSeverityFilter, alertTypeFilter, alertSearch, alertDatePreset, alertDateRangeIso,
    eventRulesSearchTerm, eventRulesSelectedIds,
    historyDevice, historyTimezoneMode, historyDatePreset, historyDateRangeIso,
    splitMode, stopThresholdSecs, idleThresholdSecs,
    historyMapCenter, historyMapZoom, historyShowPOI, historyDarkMode,
    showRouteOverlay, showDataPoints, showIdleIcons, showStoppedIcons, showEventIcons, snapToRoadEnabled,
    forensicActiveTab, messageViewMode, graphEnabledFields, graphCustomColors,
    invAddress, invLat, invLng, invDatePreset, invDateRangeIso, invRadius,
    selectedReportType, reportFormat, reportDateRangeIso,
  ]);

  return (
    <AnalyticsContext.Provider value={{
      persistedActiveTab, setPersistedActiveTab,
      mobileHistoryView, setMobileHistoryView,
      mobileInvestigationView, setMobileInvestigationView,

      eventFilter, setEventFilter,
      eventStatusFilter, setEventStatusFilter,
      eventSeverityFilter, setEventSeverityFilter,
      eventSearch, setEventSearch,
      eventDatePreset, setEventDatePreset,
      eventDateRangeIso, setEventDateRangeIso,
      eventsView, setEventsView,
      eventsPage, setEventsPage,

      alertFilter, setAlertFilter,
      alertSeverityFilter, setAlertSeverityFilter,
      alertTypeFilter, setAlertTypeFilter,
      alertSearch, setAlertSearch,
      alertDatePreset, setAlertDatePreset,
      alertDateRangeIso, setAlertDateRangeIso,

      eventRulesSearchTerm, setEventRulesSearchTerm,
      eventRulesSelectedIds, setEventRulesSelectedIds,

      historyDevice, setHistoryDevice,
      historyTimezoneMode, setHistoryTimezoneMode,
      historyDatePreset, setHistoryDatePreset,
      historyDateRangeIso, setHistoryDateRangeIso,
      splitMode, setSplitMode,
      stopThresholdSecs, setStopThresholdSecs,
      idleThresholdSecs, setIdleThresholdSecs,

      historyMapCenter, setHistoryMapCenter,
      historyMapZoom, setHistoryMapZoom,
      historyShowPOI, setHistoryShowPOI,
      historyDarkMode, setHistoryDarkMode,

      showRouteOverlay, setShowRouteOverlay,
      showDataPoints, setShowDataPoints,
      showIdleIcons, setShowIdleIcons,
      showStoppedIcons, setShowStoppedIcons,
      showEventIcons, setShowEventIcons,
      snapToRoadEnabled, setSnapToRoadEnabled,

      forensicActiveTab, setForensicActiveTab,
      messageViewMode, setMessageViewMode,
      graphEnabledFields, setGraphEnabledFields,
      graphCustomColors, setGraphCustomColors,

      invAddress, setInvAddress,
      invLat, setInvLat,
      invLng, setInvLng,
      invDatePreset, setInvDatePreset,
      invDateRangeIso, setInvDateRangeIso,
      invRadius, setInvRadius,

      selectedReportType, setSelectedReportType,
      reportFormat, setReportFormat,
      reportDateRangeIso, setReportDateRangeIso,
    }}>
      {children}
    </AnalyticsContext.Provider>
  );
};
